// DDI Trajectory Calculator
// Ported from engineer prototype (untracked/NEW-modal/)
// Directional drilling mathematics for DDI calculation

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

const CONV = 3.28084; // 1 m = 3.28084 ft

// Dogleg angle between two survey stations
const Dl = (inclK, inclK1, aziK, aziK1) => {
  const ik = toRad(inclK);
  const ik1 = toRad(inclK1);
  const ak = toRad(aziK);
  const ak1 = toRad(aziK1);
  const term = Math.sin((ik1 - ik) / 2) ** 2 +
    Math.sin(ik) * Math.sin(ik1) * Math.sin((ak1 - ak) / 2) ** 2;
  return toDeg(2 * Math.asin(Math.sqrt(term)));
};

// Shape factor for minimum curvature method
const shapeFactor = (dlDeg) => {
  const dl = toRad(dlDeg);
  if (dl >= 0.02) return Math.tan(dl / 2) / (dl / 2);
  const T1 = dl ** 2 / 12;
  const T2 = dl ** 2 / 10;
  const T3 = dl ** 2 / 168;
  const T4 = (31 * dl ** 2) / 18;
  return 1 + T1 * (1 + T2 * (1 + T3 * (1 + T4)));
};

const dispX = (dxK, mdK, mdK1, inclK, inclK1, aziK, aziK1, sf) => {
  const dMd = mdK1 - mdK;
  return dxK + ((dMd * sf) / 2) * (Math.sin(toRad(inclK)) * Math.sin(toRad(aziK)) +
    Math.sin(toRad(inclK1)) * Math.sin(toRad(aziK1)));
};

const dispY = (dyK, mdK, mdK1, inclK, inclK1, aziK, aziK1, sf) => {
  const dMd = mdK1 - mdK;
  return dyK + ((dMd * sf) / 2) * (Math.sin(toRad(inclK)) * Math.cos(toRad(aziK)) +
    Math.sin(toRad(inclK1)) * Math.cos(toRad(aziK1)));
};

const dispZ = (dzK, mdK, mdK1, inclK, inclK1, sf) => {
  const dMd = mdK1 - mdK;
  return dzK + ((dMd * sf) / 2) * (Math.cos(toRad(inclK)) + Math.cos(toRad(inclK1)));
};

const ahd = (dxK1, dxK, dyK, ahdK, dyK1) => {
  const dx = dxK1 - dxK;
  const dy = dyK1 - dyK;
  return ahdK + Math.sqrt(dx * dx + dy * dy);
};

const tort = (dl, prevTort) => prevTort + dl;

const ddi = (ahdK1, mdK1, dzK1, tortK1) => {
  if (ahdK1 !== 0 && tortK1 !== 0) {
    return Math.log10((CONV * mdK1 * ahdK1 * tortK1) / dzK1);
  }
  return 0;
};

// Main computation engine
export const computeTrajectory = (data, useImperial = false) => {
  const results = [];
  let mdK = useImperial ? data[0].md / CONV : data[0].md;
  let dxK = 0, dyK = 0, dzK = mdK, ahdK = 0, tortK = 0;

  results.push({
    tvd: useImperial ? dzK * CONV : dzK,
    ahd: useImperial ? ahdK * CONV : ahdK,
    tort: tortK,
    ddi: 0,
  });

  for (let i = 1; i < data.length; i++) {
    const mdK1 = useImperial ? data[i].md / CONV : data[i].md;
    const inclK = data[i - 1].inc, inclK1 = data[i].inc;
    const aziK = data[i - 1].azi, aziK1 = data[i].azi;

    const dl = Dl(inclK, inclK1, aziK, aziK1);
    const sf = shapeFactor(dl);

    const dxK1 = dispX(dxK, mdK, mdK1, inclK, inclK1, aziK, aziK1, sf);
    const dyK1 = dispY(dyK, mdK, mdK1, inclK, inclK1, aziK, aziK1, sf);
    const dzK1 = dispZ(dzK, mdK, mdK1, inclK, inclK1, sf);
    const ahdK1 = ahd(dxK1, dxK, dyK, ahdK, dyK1);
    const tortK1 = tort(dl, tortK);
    const ddiK1 = ddi(ahdK1, mdK1, dzK1, tortK1);

    results.push({
      tvd: useImperial ? dzK1 * CONV : dzK1,
      ahd: useImperial ? ahdK1 * CONV : ahdK1,
      tort: tortK1,
      ddi: ddiK1,
    });

    mdK = mdK1; dxK = dxK1; dyK = dyK1; dzK = dzK1; ahdK = ahdK1; tortK = tortK1;
  }

  const maxDDI = Math.max(...results.map((r) => r.ddi));
  const maxTVD = Math.max(...results.map((r) => r.tvd));
  const maxAHD = Math.max(...results.map((r) => r.ahd));
  const maxTort = Math.max(...results.map((r) => r.tort));

  return {
    output: results,
    maxDDI: Number(maxDDI.toFixed(2)),
    maxTVD: Number(maxTVD.toFixed(2)),
    maxAHD: Number(maxAHD.toFixed(2)),
    maxTort: Number(maxTort.toFixed(2)),
    classification: classifyDDI(maxDDI).label,
  };
};

// Generate gradual inclination change (bend section)
export const generateBend = (kop, br, incStart, incEnd, useImperial = false, nrOfSegments = 20) => {
  const rate = useImperial ? 100 : 30;
  const results = [];
  const deltaInc = (incEnd - incStart) / nrOfSegments;

  for (let i = 0; i <= nrOfSegments; i++) {
    results.push({
      md: kop + ((i * Math.abs(deltaInc)) / br) * rate,
      inc: incStart + i * deltaInc,
      azi: 0,
    });
  }
  return results;
};

// Generate J-shape trajectory
export const generateJShape = (kop, br, inc, tdmd, useImperial = false) => {
  const rate = useImperial ? 100 : 30;
  const eob = kop + (inc / br) * rate;

  if (eob >= tdmd) {
    throw new Error(
      `Build section longer than TD MD. Inclination not reached before end of well. Increase TD MD above ${eob.toFixed(1)}`
    );
  }

  return [
    { md: 0, inc: 0, azi: 0 },
    ...generateBend(kop, br, 0, inc, useImperial),
    { md: tdmd, inc, azi: 0 },
  ];
};

// Generate S-shape / double build trajectory
export const generateSShape = (kop1, br1, inc1, kop2, br2, inc2, tdmd, useImperial = false) => {
  const rate = useImperial ? 100 : 30;
  const eob1 = kop1 + (inc1 / br1) * rate;
  const eob2 = kop2 + (Math.abs(inc2 - inc1) / br2) * rate;

  if (eob1 >= kop2) {
    throw new Error(
      `First build section extends past KOP 2. Increase KOP 2 above ${eob1.toFixed(1)}`
    );
  }
  if (eob2 >= tdmd) {
    throw new Error(
      `Second build/drop section longer than TD MD. Increase TD MD above ${eob2.toFixed(1)}`
    );
  }

  return [
    { md: 0, inc: 0, azi: 0 },
    ...generateBend(kop1, br1, 0, inc1, useImperial),
    ...generateBend(kop2, br2, inc1, inc2, useImperial),
    { md: tdmd, inc: inc2, azi: 0 },
  ];
};

// Parse CSV text into survey array
export const parseCSV = (text) => {
  const rows = text.trim().split(/\r?\n/);
  return rows.slice(1).map((line) => {
    const parts = line.split(/[,;]+/);
    return {
      md: parseFloat(parts[0]),
      inc: parseFloat(parts[1]),
      azi: parseFloat(parts[2]),
    };
  }).filter((p) => !isNaN(p.md) && !isNaN(p.inc) && !isNaN(p.azi));
};

// Map DDI value to settings key
export const classifyDDI = (ddiValue) => {
  if (ddiValue < 6) return { key: 'verylow', label: 'Low' };
  if (ddiValue < 6.4) return { key: 'low', label: 'Medium' };
  if (ddiValue < 6.8) return { key: 'medium', label: 'High' };
  return { key: 'high', label: 'Very High' };
};
